const KEYCLOAK_URL = process.env.VUE_APP_KEYCLOAK_URL;
const REALM = process.env.VUE_APP_KEYCLOAK_REALM;
const CLIENT_ID = process.env.VUE_APP_KEYCLOAK_CLIENT_ID;
const LOGIN_FLOW = (process.env.VUE_APP_KEYCLOAK_LOGIN_FLOW || 'popup').trim();

import store from "@/store";

/**
 * Construye la URL de autenticación de Keycloak.
 */
export const buildAuthUrl = (redirectUri, prompt = null, action = null, maxAge = null) => {
  if (!KEYCLOAK_URL || !REALM || !CLIENT_ID) {
    console.error("Configuración de Keycloak incompleta en .env");
    return "";
  }

  const url = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'id_token token',
    scope: 'openid profile email',
    nonce: Math.random().toString(36).substring(7)
  });

  if (prompt) {
    params.append('prompt', prompt);
  }

  if (action) {
    params.append('kc_action', action);
  }

  if (maxAge !== null) {
    params.append('max_age', maxAge);
  }

  return `${url}?${params.toString()}`;
};

/**
 * Construye la URL para verificar la sesión silenciosamente (prompt=none).
 */
export const buildSilentCheckUrl = () => {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const url = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'id_token token',
    scope: 'openid profile email',
    prompt: 'none'
  });

  return `${url}?${params.toString()}`;
};

/**
 * Realiza una comprobación silenciosa de SSO.
 */
export const checkSilentLogin = () => {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = buildSilentCheckUrl();

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "keycloak-token" || event.data?.type === "keycloak-error") {
        window.removeEventListener("message", handleMessage);
        document.body.removeChild(iframe);
        resolve(event.data);
      }
    };

    window.addEventListener("message", handleMessage);
    document.body.appendChild(iframe);

    setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
      resolve({ type: "keycloak-error", error: "timeout" });
    }, 5000);
  });
};

/**
 * Abre una ventana emergente para el login de Keycloak.
 */
export const openLoginPopup = () => {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const url = buildAuthUrl(redirectUri, 'login', null, 0);

  if (!url) return null;

  const width = 400;
  const height = 700;
  const left = (window.screen.availWidth - width) / 2;
  const top = (window.screen.availHeight - height) / 2;

  return window.open(
    url,
    'KeycloakLogin',
    `width=${width},height=${height},left=${left},top=${top},status=no,menubar=no,toolbar=no`
  );
};

/**
 * Inicia el proceso de login según la configuración.
 */
export const initiateKeycloakLogin = () => {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const url = buildAuthUrl(redirectUri);

  if (!url) return;

  if (LOGIN_FLOW === 'standard') {
    window.location.href = url;
  } else {
    openLoginPopup();
  }
};

/**
 * Cierra la sesión de Keycloak.
 */
export const logoutFromKeycloak = (idTokenHint = null) => {
  const origin = window.location.origin;
  
  // Respaldo robusto: Store -> LocalStorage Fallback
  const storeIdToken = store.getters.getUser?.idToken;
  const finalIdToken = idTokenHint || storeIdToken || localStorage.getItem("keycloak_id_token");
  
  const params = new URLSearchParams();
  params.append('post_logout_redirect_uri', origin);
  params.append('client_id', CLIENT_ID);

  if (finalIdToken) {
    params.append('id_token_hint', finalIdToken);
  }

  const logoutUrl = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/logout?${params.toString()}`;
  window.location.href = logoutUrl;
};
